import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { PAYMENT_ELIGIBILITY_ERROR_MESSAGES, SAME_DAY_RESTRICTION_MESSAGES } from '@ld.consumer-portal/constants';
import { ENVIRONMENT, type HelocLoanInfo, type LoanResponse } from '@ld.consumer-portal/models';
import {
  checkSpecialLoanStatus,
  isAutopaySetupAllowedAfterDeletion,
  isDateHolidayOrWeekend,
  isNextDayAfterDateNonBusinessDay,
  wasDeletionBeforeCutoff,
  type LoanStatusCheckResult,
} from './autopay-utils';
import { PaymentEligibilityService, STOP_CODE_BLOCKED } from './payment-eligibility.service';
import { checkStopCodesSimple } from './eligibility-utils';

export interface FullAutopayEligibilityResult {
  isEligible: boolean;
  eligibilityType: 'inhouse' | 'repay' | 'none';
  canSetupMonthly: boolean;
  canSetupBiweekly: boolean;
  canSetupSemimonthly: boolean;
  errors: string[];
  warnings: string[];
  maxAdditionalPrincipal: number;
  maxBiweeklyAdditionalPrincipal: number;
  maxSemimonthlyAdditionalPrincipal: number;
  specialStatus?: LoanStatusCheckResult;
  isHeloc: boolean;
  isInvestorIdStop: boolean;
  isPaymentDueDateHardStop: boolean;
  daysPastDue: number;
  minPayOnDate: string | null;
  maxPayOnDate: string | null;
  firstBiweeklyPayOnDate: string | null;
  secondBiweeklyPayOnDate: string | null;
  semiMonthlyPayOnDate: string | null;
}

const DEFAULT_MAX_ADDITIONAL_PRINCIPAL = 9999.99;

@Injectable({
  providedIn: 'root',
})
export class AutopayEligibilityService {
  private readonly paymentEligibilityService = inject(PaymentEligibilityService);
  private readonly env = inject(ENVIRONMENT);
  private readonly translocoService = inject(TranslocoService);

  checkEligibility(
    loanResponse: LoanResponse,
    lastDeletedAutopayDate?: string | null,
    helocLoanInfo?: HelocLoanInfo | null,
    hasSemiMonthlyFlag?: boolean,
  ): FullAutopayEligibilityResult {
    const loan = loanResponse.loan;
    const additionalInfo = loanResponse.loanAdditionalInfo;
    const errors: string[] = [];
    const warnings: string[] = [];
    const isHeloc = !!helocLoanInfo || loan.productType?.toUpperCase() === 'HELOC';

    const monthlyEligibility =
      isHeloc && helocLoanInfo?.recurringPaymentEligibility
        ? helocLoanInfo.recurringPaymentEligibility
        : loanResponse.recurringPaymentEligibility;
    const biweeklyEligibility =
      isHeloc && helocLoanInfo?.biWeeklyRecurringPaymentEligibility
        ? helocLoanInfo.biWeeklyRecurringPaymentEligibility
        : loanResponse.biweeklyRecurringPaymentEligibility;
    const semimonthlyEligibility =
      isHeloc && helocLoanInfo?.semiMonthlyRecurringPaymentEligibility
        ? helocLoanInfo.semiMonthlyRecurringPaymentEligibility
        : loanResponse.semiMonthlyRecurringPaymentEligibility;

    if (!isHeloc) {
      const stopCodeErrors = this.checkStopCodes(loan);
      errors.push(...stopCodeErrors);
    }

    const monthlyAllowed = monthlyEligibility?.isEligibile === 'Allowed';
    const biweeklyAllowed = biweeklyEligibility?.isEligibile === 'Allowed';
    const semimonthlyAllowed = semimonthlyEligibility?.isEligibile === 'Allowed';

    const isInvestorIdStop = loan.investorId === 'W01' && this.hasInvestorIdStopError(monthlyEligibility);
    const isPaymentDueDateHardStop = this.hasPaymentDueDateHardStop(monthlyEligibility);

    if (!monthlyAllowed && monthlyEligibility?.errors?.length) {
      const mappedErrors = monthlyEligibility.errors.map((e) => this.mapErrorCode(e));
      errors.push(...mappedErrors);
    }

    if (!biweeklyAllowed && biweeklyEligibility?.errors?.length) {
      const mappedWarnings = biweeklyEligibility.errors.map((e) => this.mapErrorCode(e));
      if (monthlyAllowed) {
        warnings.push(...mappedWarnings.map((w) => `${w}`));
      }
    }

    if (hasSemiMonthlyFlag && !semimonthlyAllowed && semimonthlyEligibility?.errors?.length) {
      const mappedWarnings = semimonthlyEligibility.errors.map((e) => this.mapErrorCode(e));
      if (monthlyAllowed) {
        warnings.push(...mappedWarnings.map((w) => `${w}`));
      }
    }

    let specialStatus: LoanStatusCheckResult = { isEligible: true, canSetupMonthly: true, canSetupBiweekly: true };
    if (!isHeloc) {
      const hasActiveMitigationPlan =
        (loan.mitigationInfo?.isActiveForbearancePlan ||
          loan.mitigationInfo?.isActiveRepaymentPlan ||
          loan.mitigationInfo?.isActiveTrialPlan) ??
        false;

      specialStatus = checkSpecialLoanStatus({
        daysPastDue: loan.daysPastDue,
        isForeclosure: loan.foreclosureStopCode === STOP_CODE_BLOCKED.foreclosure,
        isForbearance: loan.mitigationInfo?.isActiveForbearancePlan ?? false,
        isBankruptcy: loan.isCh11_12_13Discharge,
        isLossMitigation: hasActiveMitigationPlan && !!loan.lossMitigationIndicator,
        isTransferred: loan.isTransferredToCenlar || loan.isTransferredToThirdParty,
      });

      if (!specialStatus.isEligible && !monthlyAllowed && specialStatus.message) {
        errors.push(specialStatus.message);
      } else if (!specialStatus.isEligible && monthlyAllowed && specialStatus.message) {
        warnings.push(specialStatus.message);
      } else if (specialStatus.message) {
        warnings.push(specialStatus.message);
      }
    }

    if (lastDeletedAutopayDate && !isAutopaySetupAllowedAfterDeletion(lastDeletedAutopayDate)) {
      let messageKey: string;
      if (isDateHolidayOrWeekend(lastDeletedAutopayDate)) {
        messageKey = SAME_DAY_RESTRICTION_MESSAGES.processingDelete2Days;
      } else if (isNextDayAfterDateNonBusinessDay(lastDeletedAutopayDate)) {
        messageKey = wasDeletionBeforeCutoff(lastDeletedAutopayDate)
          ? SAME_DAY_RESTRICTION_MESSAGES.processingDelete1Day
          : SAME_DAY_RESTRICTION_MESSAGES.processingDelete2Days;
      } else {
        messageKey = SAME_DAY_RESTRICTION_MESSAGES.processingDelete1Day;
      }
      errors.push(this.translocoService.translate(messageKey));
    }

    if (!isHeloc && loan.isRetainedLoan === false) {
      errors.push(this.translocoService.translate('servicesErrorsAutopayNotAvailable'));
    }

    const eligibilityType = this.determineEligibilityType(loan, isHeloc);

    const maxAdditionalPrincipal = additionalInfo?.maxAdditionalPrincipalAllowed ?? DEFAULT_MAX_ADDITIONAL_PRINCIPAL;
    const maxBiweeklyAdditionalPrincipal =
      additionalInfo?.maxBiweeklyAdditionalPrincipalAllowed ?? DEFAULT_MAX_ADDITIONAL_PRINCIPAL;
    const maxSemimonthlyAdditionalPrincipal =
      additionalInfo?.maxSemiMonthlyAdditionalPrincipalAllowed ?? DEFAULT_MAX_ADDITIONAL_PRINCIPAL;

    const canSetupMonthly = monthlyAllowed && errors.length === 0;
    const canSetupBiweekly = biweeklyAllowed && specialStatus.canSetupBiweekly && errors.length === 0;
    const canSetupSemimonthly = semimonthlyAllowed && specialStatus.canSetupBiweekly && errors.length === 0;

    return {
      isEligible: errors.length === 0 && (monthlyAllowed || biweeklyAllowed || semimonthlyAllowed),
      eligibilityType,
      canSetupMonthly,
      canSetupBiweekly,
      canSetupSemimonthly,
      errors,
      warnings: [...new Set(warnings)],
      maxAdditionalPrincipal,
      maxBiweeklyAdditionalPrincipal,
      maxSemimonthlyAdditionalPrincipal,
      specialStatus,
      isHeloc,
      isInvestorIdStop,
      isPaymentDueDateHardStop,
      daysPastDue: loan.daysPastDue ?? 0,
      minPayOnDate: additionalInfo?.minPayOnDate ?? null,
      maxPayOnDate: additionalInfo?.maxPayOnDate ?? null,
      firstBiweeklyPayOnDate: additionalInfo?.firstBiweeklyPayOnDate ?? null,
      secondBiweeklyPayOnDate: additionalInfo?.secondBiweeklyPayOnDate ?? null,
      semiMonthlyPayOnDate: additionalInfo?.semiMonthlyPayOnDate ?? null,
    };
  }

  private hasPaymentDueDateHardStop(
    eligibility: LoanResponse['recurringPaymentEligibility'] | HelocLoanInfo['recurringPaymentEligibility'] | undefined,
  ): boolean {
    if (!eligibility?.errors?.length || eligibility.isEligibile === 'Allowed') return false;
    return eligibility.errors.some(
      (e) => typeof e !== 'string' && e.errorCode === 'PaymentDueDateBelongsToNextThreeMonth' && e.exceptionType === 'HardStop',
    );
  }

  private hasInvestorIdStopError(
    eligibility: LoanResponse['recurringPaymentEligibility'] | HelocLoanInfo['recurringPaymentEligibility'] | undefined,
  ): boolean {
    if (!eligibility?.errors?.length || eligibility.isEligibile === 'Allowed') return false;
    return eligibility.errors.some(
      (e) => typeof e !== 'string' && e.errorCode === 'InvestorIdStop' && e.exceptionType === 'HardStop',
    );
  }

  private checkStopCodes(loan: LoanResponse['loan']): string[] {
    return checkStopCodesSimple(loan, {
      badCheckStopCode: this.translocoService.translate('servicesErrorsPaymentBadCheckStopCode'),
      paidInFull: this.translocoService.translate('servicesErrorsPaymentPaidInFull'),
      foreclosure: this.translocoService.translate('servicesErrorsPaymentForeclosure'),
      processStopCode: this.translocoService.translate('servicesErrorsPaymentTemporarilyUnavailable'),
    });
  }

  private determineEligibilityType(loan: LoanResponse['loan'], isHeloc = false): 'inhouse' | 'repay' | 'none' {
    const helocInHouseEnabled = !!this.env.appSettings.enableHelocAutopay;

    if (isHeloc) {
      if (helocInHouseEnabled) {
        return 'inhouse';
      }
      return loan.isRepayAutopayEnabled ? 'repay' : 'none';
    }

    if (loan.isInhouseAutopayEnabled) {
      return 'inhouse';
    }

    if (loan.isRepayAutopayEnabled) {
      return 'repay';
    }

    return 'none';
  }

  private mapErrorCode(
    error: string | { message?: string; code?: string; errorCode?: string; errorMessage?: string },
  ): string {
    const errorCode =
      typeof error === 'string' ? error : (error.errorCode ?? error.code ?? error.errorMessage ?? error.message);
    const errorMessage =
      typeof error === 'string' ? error : (error.errorMessage ?? error.message ?? error.errorCode ?? error.code);

    if (!errorCode && !errorMessage) {
      return this.translocoService.translate('servicesErrorsGenericContactCustomerService');
    }

    return PAYMENT_ELIGIBILITY_ERROR_MESSAGES[errorCode ?? ''] ?? errorMessage ?? errorCode ?? '';
  }

  shouldRedirectToRepay(loanResponse: LoanResponse): boolean {
    const eligibility = this.checkEligibility(loanResponse);
    return eligibility.eligibilityType === 'repay';
  }

  getAutopayDestination(
    loanResponse: LoanResponse,
    loanNumber: string,
    repayBaseUrl?: string,
  ): { type: 'internal' | 'external' | 'blocked'; path: string; reason?: string } {
    const eligibility = this.checkEligibility(loanResponse);

    if (!eligibility.isEligible) {
      return {
        type: 'blocked',
        path: `/loans/${loanNumber}`,
        reason: eligibility.errors[0] ?? this.translocoService.translate('servicesErrorsAutopayNotAvailable'),
      };
    }

    if (eligibility.eligibilityType === 'repay') {
      const baseUrl =
        repayBaseUrl ?? this.env.appSettings.repayUrl ?? 'https://uat-loandepotwebpay.secureconduit.net/login';
      return {
        type: 'external',
        path: `${baseUrl}?loanNumber=${loanNumber}`,
      };
    }

    return {
      type: 'internal',
      path: `/loans/${loanNumber}/autopay`,
    };
  }
}
